import { createDefaultTextStyle } from "./utils/textStyle";
import { WEB_MAKER_DEFAULT_CONTENT, getDefaultCopyrightText } from "./defaultContent";
import { createDefaultMegaMenu } from "./utils/megaMenu";
import { createDefaultHeroCta } from "./utils/heroCtas";
import { createTextSegment } from "./utils/textSegments";
import { normalizeFeaturedVehicleCardStyle } from "./utils/featuredVehicleCards";
import { normalizeFeaturedVehicleSelection } from "./utils/featuredVehicleSelection";
import { normalizeGlobalStylePresets } from "./utils/globalStylePresets";
import { WEB_MAKER_SITE_SCHEMA_VERSION } from "./utils/siteConfigSchema";

export const WEB_MAKER_STORAGE_KEY = "dealertiq-web-maker-site";

export const WEB_MAKER_TABS = {
  PAGES: "pages",
  SECTIONS: "sections",
  PREVIEW: "preview",
  THEME: "theme",
  CATALOG: "catalog",
  WEBSITE: "website",
  SETTINGS: "settings",
};

export const SECTION_TYPES = [
  "empty_section",
  "alert_banner",
  "hero",
  "about",
  "dealer_location",
  "dealer_contact_intro",
  "services",
  "inventory_cta",
  "inventory_quick_search",
  "featured_vehicles",
  "inventory_browser",
  "financing_calculator",
  "contact",
  "footer",
];

export const FONT_FAMILY_OPTIONS = [
  { value: "sans-serif", label: "Sans Serif" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia" },
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "'Segoe UI', sans-serif", label: "Segoe UI" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
  { value: "'Trebuchet MS', sans-serif", label: "Trebuchet MS" },
  { value: "'Courier New', Courier, monospace", label: "Courier New" },
  { value: "Impact, Haettenschweiler, sans-serif", label: "Impact" },
];

export const TEXT_SIZE_OPTIONS = [
  { value: "", labelKey: "default" },
  { value: "14px", labelKey: "small" },
  { value: "16px", labelKey: "medium" },
  { value: "20px", labelKey: "large" },
  { value: "24px", labelKey: "xlarge" },
  { value: "32px", labelKey: "heading" },
];

const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const defaultStyle = () => createDefaultTextStyle();
const { sectionData: SECTION_DEFAULTS } = WEB_MAKER_DEFAULT_CONTENT;

const sectionBackgroundDefaults = () => ({
  backgroundColor: "",
  backgroundImageUrl: "",
  backgroundImageFit: "",
});

export const createDefaultSection = (type) => {
  const base = { id: createId(), type, enabled: true };
  const d = SECTION_DEFAULTS[type] || {};

  switch (type) {
    case "empty_section":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          minHeight: Number(d.minHeight) || 360,
        },
      };
    case "alert_banner":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          backgroundColor: d.backgroundColor || "#ffffff",
          message: d.message || "",
          messageStyle: defaultStyle(),
          messageSegments: (d.messageSegments || []).map((segment) =>
            createTextSegment({
              text: segment.text || "",
              textStyle: segment.textStyle || defaultStyle(),
            })
          ),
        },
      };
    case "hero":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          headline: d.headline || "",
          headlineStyle: defaultStyle(),
          subheadline: d.subheadline || "",
          subheadlineStyle: defaultStyle(),
          subheadlineSegments: (d.subheadlineSegments || []).map((segment) =>
            createTextSegment({
              text: segment.text || "",
              textStyle: segment.textStyle || defaultStyle(),
            })
          ),
          ctas: (d.ctas && d.ctas.length
            ? d.ctas
            : [
                createDefaultHeroCta({
                  text: d.ctaText || "View Inventory",
                  link: d.ctaLink || "/inventory",
                }),
              ]
          ).map((cta) =>
            createDefaultHeroCta({
              text: cta.text || "",
              link: cta.link || "",
              textStyle: cta.textStyle || defaultStyle(),
            })
          ),
          // Legacy single-CTA fields kept for older saved sites / presets
          ctaText: d.ctaText || "View Inventory",
          ctaTextStyle: defaultStyle(),
          ctaLink: d.ctaLink || "/inventory",
        },
      };
    case "about":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "",
          titleStyle: defaultStyle(),
          body: d.body || "",
          bodyStyle: defaultStyle(),
          imageUrl: "",
          imageAlt: "",
          imageFit: "",
        },
      };
    case "dealer_location":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "Location",
          titleStyle: defaultStyle(),
          directionsText: d.directionsText || "Get directions",
          directionsTextStyle: defaultStyle(),
          ctas: (d.ctas || []).map((cta) =>
            createDefaultHeroCta({
              text: cta.text || "",
              link: cta.link || "",
              textStyle: cta.textStyle || defaultStyle(),
            })
          ),
        },
      };
    case "dealer_contact_intro":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "Contact Us",
          titleStyle: defaultStyle(),
          description: d.description || "",
          descriptionStyle: defaultStyle(),
          requiredNote:
            d.requiredNote || "Fields marked with asterisk (*) are required",
          requiredNoteStyle: defaultStyle(),
        },
      };
    case "services":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "",
          titleStyle: defaultStyle(),
          items: (d.items || []).map((item) => ({
            title: item.title || "",
            titleStyle: defaultStyle(),
            description: item.description || "",
            descriptionStyle: defaultStyle(),
          })),
        },
      };
    case "inventory_cta":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "",
          titleStyle: defaultStyle(),
          description: d.description || "",
          descriptionStyle: defaultStyle(),
          ctaText: d.ctaText || "Browse Inventory",
          ctaTextStyle: defaultStyle(),
          ctaLink: d.ctaLink || "/inventory",
        },
      };
    case "inventory_quick_search":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "Let's Get Started",
          titleStyle: defaultStyle(),
          typeOptions: d.typeOptions || ["All Types", "Used", "Dealer Certified"],
          makeOptions: d.makeOptions || ["All Makes"],
          yearOptions: d.yearOptions || ["All Years"],
          selectedType: d.selectedType || "",
          selectedMake: d.selectedMake || "",
          selectedYear: d.selectedYear || "",
          ctaText: d.ctaText || "View Inventory",
          ctaTextStyle: defaultStyle(),
          ctaLink: d.ctaLink || "/inventory",
          secondaryCtas: (d.secondaryCtas || []).map((cta) =>
            createDefaultHeroCta({
              text: cta.text || "",
              link: cta.link || "",
              textStyle: cta.textStyle || defaultStyle(),
            })
          ),
        },
      };
    case "featured_vehicles":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "Featured Vehicles",
          titleStyle: defaultStyle(),
          cardStyle: normalizeFeaturedVehicleCardStyle(d.cardStyle),
          vehicleSelection: normalizeFeaturedVehicleSelection(
            d.vehicleSelection,
            d
          ),
          limit: typeof d.limit === "number" ? d.limit : 6,
          sortBy: d.sortBy || "relevance",
          ctaText: d.ctaText || "Full Inventory",
          ctaTextStyle: defaultStyle(),
          ctaLink: d.ctaLink || "/inventory",
        },
      };
    case "inventory_browser":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "",
          titleStyle: defaultStyle(),
          showFilters: d.showFilters !== false,
          inventoryFilters: d.inventoryFilters || null,
          viewMode: d.viewMode || "grid",
          sortBy: d.sortBy || "relevance",
          pageSize: typeof d.pageSize === "number" ? d.pageSize : 12,
          contentWidth:
            Number.isFinite(Number(d.contentWidth)) && Number(d.contentWidth) > 0
              ? Number(d.contentWidth)
              : 1200,
          minHeight:
            Number.isFinite(Number(d.minHeight)) && Number(d.minHeight) > 0
              ? Number(d.minHeight)
              : 560,
        },
      };
    case "financing_calculator":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "Estimate Your Payments",
          titleStyle: defaultStyle(),
          description: d.description || "",
          descriptionStyle: defaultStyle(),
          price: Number.isFinite(Number(d.price)) ? Number(d.price) : 20000,
          primaryCtaLabel: d.primaryCtaLabel || "Get Pre-Approved",
          primaryCtaLabelStyle: defaultStyle(),
          primaryCtaHref: d.primaryCtaHref || "/contact?intent=financing",
          initialCreditRange: d.initialCreditRange || "650-699",
          initialTermMonths: Number(d.initialTermMonths) || 60,
          minHeight: Number(d.minHeight) || 620,
        },
      };
    case "contact":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          title: d.title || "",
          titleStyle: defaultStyle(),
          showMap: false,
          mapEmbedUrl: "",
        },
      };
    case "footer":
      return {
        ...base,
        data: {
          ...sectionBackgroundDefaults(),
          copyrightText: getDefaultCopyrightText(),
          copyrightTextStyle: defaultStyle(),
          socialTextStyle: defaultStyle(),
          facebookUrl: "",
          instagramUrl: "",
          twitterUrl: "",
          columnDistribution: "",
          useOnAllPages: false,
          columns: (d.columns || []).map((column) => ({
            id: column.id || createId(),
            title: column.title || "",
            titleStyle: column.titleStyle || defaultStyle(),
            showContactDetails: Boolean(column.showContactDetails),
            links: (column.links || []).map((link) => ({
              id: link.id || createId(),
              label: link.label || "",
              url: link.url || "",
              labelStyle: link.labelStyle || defaultStyle(),
            })),
          })),
        },
      };
    default:
      return base;
  }
};

export const createDefaultSiteConfig = () => {
  const settingsDefaults = WEB_MAKER_DEFAULT_CONTENT.settings;
  const vehicleDetailsPageDefaults =
    WEB_MAKER_DEFAULT_CONTENT.pageData.vehicle_details;
  return {
  schemaVersion: WEB_MAKER_SITE_SCHEMA_VERSION,
  themeId: null,
  layoutVariant: "default",
  removedPageKeys: [],
  settings: {
    businessName: settingsDefaults.businessName,
    businessNameStyle: defaultStyle(),
    tagline: settingsDefaults.tagline,
    taglineStyle: defaultStyle(),
    phone: settingsDefaults.phone,
    phoneStyle: defaultStyle(),
    email: settingsDefaults.email,
    emailStyle: defaultStyle(),
    address: settingsDefaults.address,
    addressStyle: defaultStyle(),
    city: settingsDefaults.city,
    state: "",
    zip: settingsDefaults.zip,
    locationStyle: defaultStyle(),
    hours: settingsDefaults.hours,
    hoursStyle: defaultStyle(),
    websiteUrl: "",
  },
  branding: {
    primaryColor: "#0092E0",
    secondaryColor: "#1E1E25",
    pageBackgroundColor: "#ffffff",
    headerBackgroundColor: "",
    headerBackgroundImageUrl: "",
    headerBackgroundImageFit: "",
    headerBackgroundImageLayout: {},
    headerPosition: "normal",
    headerBusinessNameHidden: false,
    headerLogoHidden: false,
    headerNavigationHidden: false,
    headerContactHidden: false,
    headerCtaLabel: "",
    headerCtaUrl: "",
    headerCtaTarget: "_self",
    headerCtaHidden: false,
    headerSocialsVisible: false,
    headerFacebookUrl: "",
    headerInstagramUrl: "",
    headerTwitterUrl: "",
    mobileHeaderShowBusinessName: true,
    mobileHeaderShowPhone: false,
    mobileHeaderShowCta: false,
    dealerMobileInventoryLabelStyle: defaultStyle(),
    dealerMobileDirectionsLabelStyle: defaultStyle(),
    dealerMobileCallLabelStyle: defaultStyle(),
    logoUrl: "",
    logoAlt: "",
    logoFit: "",
    logoLayout: {},
    fontFamily: "sans-serif",
    customFonts: [],
    stylePresets: normalizeGlobalStylePresets(),
    themeContainers: {},
    removedThemeContainerKeys: [],
  },
  pages: {
    home: { enabled: true, showInNavigation: true, label: "Home", labelStyle: defaultStyle(), megaMenu: createDefaultMegaMenu() },
    about: { enabled: true, showInNavigation: true, label: "About", labelStyle: defaultStyle(), megaMenu: createDefaultMegaMenu() },
    inventory: { enabled: true, showInNavigation: true, label: "Inventory", labelStyle: defaultStyle(), megaMenu: createDefaultMegaMenu() },
    vehicle_details: {
      enabled: true,
      showInNavigation: false,
      label: "Vehicle Details",
      template: "vehicle_detail",
      data: { ...vehicleDetailsPageDefaults },
      labelStyle: defaultStyle(),
      megaMenu: createDefaultMegaMenu(),
    },
    contact: { enabled: true, showInNavigation: true, label: "Contact", labelStyle: defaultStyle(), megaMenu: createDefaultMegaMenu() },
  },
  sections: [
    createDefaultSection("hero"),
    createDefaultSection("about"),
    createDefaultSection("services"),
    createDefaultSection("inventory_cta"),
    createDefaultSection("contact"),
    createDefaultSection("footer"),
  ],
  customTexts: [],
  customBoxes: [],
  customSearchBars: [],
  customButtons: [],
  customForms: [],
  customCarLists: [],
  customCarsForSaleFilters: [],
  customMaps: [],
  customCaptchas: [],
  customOverlayGroups: [],
  structuredLayouts: [],
};
};
